import { SearchOutlined } from "@ant-design/icons";
import React, { useRef, useState } from "react";
import Highlighter from "react-highlight-words";
import { Button, Input, Space, Table } from "antd";
import { useNavigate } from "react-router-dom";
import { autoFormatDateCell } from "../../Utils/dateFormat";

const AntDTable = ({
  tableHeaders,
  headerSelector,
  columns: providedColumns,
  tableData,
  showLoader,
  rowClickPath,
  isClickable,
  ActionBtn,
  withAction,
  loading,
  scroll,
  customFooter,
  className,
  searchExcluded = [], // headers to exclude from search feature
  pageSize,
  handleTableChange,
  filterConfig,
  totalSize,
  isSearch,
  customEmptyText,
  onRowClick, // Custom row click handler
}) => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const searchInput = useRef(null);
  // const paginationConfig = {
  //   pageSizeOptions: ["10", "20", "50", "100"], // Options for items per page
  //   showSizeChanger: true, // Show items per page changer
  //   // showQuickJumper: true, // Show quick jumper to navigate to a specific page
  //   showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`, // Show total items text
  // };
  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };
  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };
  const handleRowClick = (record) => {
    if (onRowClick) {
      // Use custom row click handler if provided
      onRowClick(record);
    } else if (isClickable) {
      const id = record.id;
      navigate(`${rowClickPath}/${id}`);
    }
  };
  const getColumnSearchProps = (dataIndex) => {
    if (!searchExcluded.includes(dataIndex)) {
      return {
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          close,
        }) => (
          <div
            style={{
              padding: 8,
            }}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <Input
              ref={searchInput}
              placeholder={`Search ${dataIndex}`}
              value={selectedKeys[0]}
              onChange={(e) =>
                setSelectedKeys(e.target.value ? [e.target.value] : [])
              }
              onPressEnter={() =>
                handleSearch(selectedKeys, confirm, dataIndex)
              }
              style={{
                marginBottom: 8,
                display: "block",
              }}
            />
            <Space>
              <Button
                type="primary"
                onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
                icon={<SearchOutlined />}
                size="small"
                style={{
                  width: 90,
                }}
              >
                Search
              </Button>
              <Button
                onClick={() => clearFilters && handleReset(clearFilters)}
                size="small"
                style={{
                  width: 90,
                }}
              >
                Reset
              </Button>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  confirm({
                    closeDropdown: false,
                  });
                  setSearchText(selectedKeys[0]);
                  setSearchedColumn(dataIndex);
                }}
              >
                Filter
              </Button>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  close();
                }}
              >
                Close
              </Button>
            </Space>
          </div>
        ),
        filterIcon: (filtered) => (
          <SearchOutlined
            style={{
              color: filtered ? "var(--sidebar-color)" : undefined,
            }}
          />
        ),
        onFilter: (value, record) => {
          const needle = value.toLowerCase();
          const raw = record[dataIndex];
          const shown = autoFormatDateCell(dataIndex, raw);
          return (
            raw?.toString().toLowerCase().includes(needle) ||
            (shown ? shown.toLowerCase().includes(needle) : false)
          );
        },
        onFilterDropdownOpenChange: (visible) => {
          if (visible) {
            setTimeout(() => searchInput.current?.select(), 100);
          }
        },
        render: (rawText) => {
          const text = autoFormatDateCell(dataIndex, rawText) ?? rawText;
          return searchedColumn === dataIndex ? (
            <Highlighter
              highlightStyle={{
                backgroundColor: "var(--sidebar-color)",
                padding: 0,
              }}
              searchWords={[searchText]}
              autoEscape
              textToHighlight={text ? text.toString() : ""}
            />
          ) : (
            text
          );
        },
      };
    }
  };

  const columns = providedColumns || tableHeaders.map((header, index) => {
    return {
      title: header,
      dataIndex: headerSelector[index],
      key: headerSelector[index],
      render: (text) => autoFormatDateCell(headerSelector[index], text) ?? text,
      ...getColumnSearchProps(headerSelector[index]),
      ...(headerSelector[index] !== "color_tag"
        ? {
            sorter: (a, b) => {
              const valueA = a[headerSelector[index]];
              const valueB = b[headerSelector[index]];

              if (typeof valueA === "string" && typeof valueB === "string") {
                return valueA.localeCompare(valueB, undefined, {
                  sensitivity: "base",
                });
              } else if (
                typeof valueA === "number" &&
                typeof valueB === "number"
              ) {
                return valueA - valueB;
              } else if (valueA instanceof Date && valueB instanceof Date) {
                return valueA - valueB;
              } else {
                return typeof valueA === "string" ? -1 : 1;
              }
            },
            sortDirections: ["descend", "ascend"],
          }
        : {}),
    };
  });
  const resolvedColumns = [...columns];
  if (withAction) {
    resolvedColumns.push({
      title: "ACTION",
      key: "action",
      width: 250,
      align: "center",
      color: "var(--sidebar-color)",
      render: (_, record) => <ActionBtn row={record} />,
    });
  }

  return (
    <Table
      columns={resolvedColumns}
      dataSource={tableData}
      onRow={(record) => ({
        onClick: () => handleRowClick(record),
      })}
      loading={loading}
      scroll={scroll}
      footer={customFooter}
      size="small"
      className="table-bg"
      pagination={{
        defaultPageSize: 10,
        showQuickJumper: true,
        showSizeChanger: true,
        total: totalSize,
        current: filterConfig?.page,
        pageSize: filterConfig?.total_records_per_page,
      }}
      onChange={handleTableChange}
      locale={{
        emptyText: customEmptyText,
      }}
    />
  );
};

export default AntDTable;
